import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Bell, UserCircle, ShieldCheck, Menu, LogOut } from "lucide-react";
import { SidebarContent } from "./Sidebar";

export default function Topbar({ title, subtitle }) {
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data || []);
    } catch {}
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const unread = notifications.filter(n => !n.read).length;

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const onClick = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    if (n.link) nav(n.link);
    load();
  };

  return (
    <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 bg-white" data-testid="topbar">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* Mobile hamburger – opens slide-out sidebar drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden -ml-2 h-11 w-11"
              data-testid="btn-mobile-menu"
              aria-label="Open navigation menu"
            >
              <Menu className="w-5 h-5 text-slate-700" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-slate-950 border-r border-slate-800" data-testid="mobile-drawer">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </SheetContent>
        </Sheet>

        <div className="min-w-0">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider truncate">{subtitle || user?.role}</div>
          <h1 className="font-display text-lg sm:text-xl md:text-2xl font-bold text-slate-900 truncate">{title}</h1>
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative h-11 w-11" data-testid="btn-notifications">
              <Bell className="w-5 h-5 text-slate-700" />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 bg-cyan-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="badge-unread">
                  {unread}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] sm:w-80 p-0 max-w-sm">
            <div className="flex items-center justify-between px-3 py-2.5 border-b">
              <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-cyan-700 hover:underline" data-testid="btn-mark-all-read">
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-auto" data-testid="notifications-list">
              {notifications.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No notifications</div>}
              {notifications.map(n => (
                <button key={n.id} onClick={() => onClick(n)} className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${n.read ? "" : "bg-cyan-50/40"}`}>
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-cyan-600"/>}
                    {n.title}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                </button>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 sm:pl-3 sm:border-l sm:border-slate-200 sm:ml-1 rounded-md hover:bg-slate-50 px-2 py-1 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 min-h-[44px]"
              data-testid="btn-profile-menu"
              aria-label="Open profile menu"
            >
              {user?.avatar_file_id ? (
                <img
                  src={`${API}/files/${user.avatar_file_id}`}
                  alt={user?.name || "Avatar"}
                  className="w-8 h-8 rounded-full object-cover border border-slate-200"
                  data-testid="topbar-avatar-img"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm" data-testid="topbar-avatar-initial">
                  {user?.name?.[0] || "?"}
                </div>
              )}
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-slate-900 leading-tight">{user?.name}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">{user?.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="profile-menu">
            <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
              Signed in as <span className="block text-slate-900 font-medium mt-0.5 truncate">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-item-profile" className="cursor-pointer">
              <UserCircle className="w-4 h-4 mr-2 text-slate-600" /> My Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/security")} data-testid="menu-item-security" className="cursor-pointer">
              <ShieldCheck className="w-4 h-4 mr-2 text-slate-600" /> Security &amp; Account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="menu-item-logout"
              className="cursor-pointer text-red-600 focus:text-red-700"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
